import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faExternalLinkAlt,
  faCalendarAlt,
  faUser,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import { faGithub as faGithubBrand } from "@fortawesome/free-brands-svg-icons";

// Helper function to extract technologies from project description
const extractTechnologies = (project) => {
  const text = (project.body || "").toLowerCase();
  const technologies = [];

  // Check for common technologies mentioned in the description
  if (text.includes("react")) technologies.push("React.js");
  if (text.includes("javascript")) technologies.push("JavaScript");
  if (text.includes("p5.js")) technologies.push("p5.js");
  if (text.includes("ml5.js")) technologies.push("ml5.js");
  if (text.includes("html")) technologies.push("HTML5");
  if (text.includes("css")) technologies.push("CSS3");
  if (text.includes("node.js") || text.includes("nodejs"))
    technologies.push("Node.js");
  if (text.includes("express")) technologies.push("Express.js");
  if (text.includes("mongodb")) technologies.push("MongoDB");
  if (text.includes("api")) technologies.push("REST API");
  if (text.includes("github api")) technologies.push("GitHub API");
  if (text.includes("machine learning")) technologies.push("Machine Learning");
  if (text.includes("algorithm")) technologies.push("Algorithms");
  if (text.includes("responsive")) technologies.push("Responsive Design");

  // If no technologies found, return default based on project type
  if (technologies.length === 0) {
    return ["JavaScript", "HTML5", "CSS3"];
  }

  return technologies;
};

// Helper function to generate features based on project description
const generateFeatures = (project) => {
  const text = (project.body || "").toLowerCase();
  const features = [];

  // Generate features based on project content
  if (text.includes("responsive"))
    features.push("Responsive design for all devices");
  if (text.includes("visual") || text.includes("visualization"))
    features.push("Interactive visual representation");
  if (text.includes("algorithm"))
    features.push("Optimized algorithm implementation");
  if (text.includes("machine learning") || text.includes("ml5"))
    features.push("Machine learning integration");
  if (text.includes("api")) features.push("RESTful API integration");
  if (text.includes("interactive")) features.push("Interactive user interface");
  if (text.includes("real-time") || text.includes("realtime"))
    features.push("Real-time processing");
  if (text.includes("performance") || text.includes("optimized"))
    features.push("Performance optimized");

  // Add default features if none found
  if (features.length === 0) {
    features.push("Modern and intuitive user interface");
    features.push("Cross-browser compatibility");
    features.push("Optimized performance");
  }

  // Always add these common features
  if (!features.some((f) => f.includes("interface"))) {
    features.push("User-friendly interface");
  }

  return features.slice(0, 4); // Limit to 4 features
};

const ProjectModal = ({ isModalOpen, setIsModalOpen, project }) => {
  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen, setIsModalOpen]);

  if (!project) return null;

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2,
      },
    },
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          className="modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={() => setIsModalOpen(false)}
        >
          <motion.div
            className="modal-container"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-header-content">
                <motion.h2 className="modal-title" variants={itemVariants}>
                  {project.title || "Project Title"}
                </motion.h2>
                <motion.p className="modal-subtitle" variants={itemVariants}>
                  {project.category || ""}
                </motion.p>
              </div>
              <motion.button
                className="modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </motion.button>
            </div>

            {/* Scrollable Content */}
            <div className="modal-scrollable-content">
              {/* Modal Body */}
              <motion.div
                className="modal-body"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
              {/* Project Image */}
              {project.img && (
                <motion.div
                  className="modal-image-container"
                  variants={itemVariants}
                  onClick={() =>
                    project.live && window.open(project.live, "_blank")
                  }
                >
                  <img
                    src={project.img}
                    alt={project.title}
                    className="modal-image"
                  />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      <span>View Live Demo</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Project Info Grid */}
              <motion.div className="project-info-grid" variants={itemVariants}>
                <div className="info-item">
                  <FontAwesomeIcon icon={faCalendarAlt} className="info-icon" />
                  <div>
                    <span className="info-label">Time Period</span>
                    <span className="info-value">
                      {project.timePeriod || "2023"}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <FontAwesomeIcon icon={faUser} className="info-icon" />
                  <div>
                    <span className="info-label">Role</span>
                    <span className="info-value">Full Stack Developer</span>
                  </div>
                </div>

                <div className="info-item">
                  <FontAwesomeIcon icon={faTag} className="info-icon" />
                  <div>
                    <span className="info-label">Status</span>
                    <span className="info-value">Completed</span>
                  </div>
                </div>
              </motion.div>

              {/* Project Description */}
              <motion.div
                className="project-description"
                variants={itemVariants}
              >
                <h3>About This Project</h3>
                <p>
                  {project.body ||
                    "This is a comprehensive project that showcases modern web development practices and innovative solutions. Built with cutting-edge technologies to deliver exceptional user experience and performance."}
                </p>
              </motion.div>

              {/* Technologies Used */}
              <motion.div
                className="project-technologies"
                variants={itemVariants}
              >
                <h3>Technologies Used</h3>
                <div className="tech-tags">
                  {(project.technologies || extractTechnologies(project)).map(
                    (tech, index) => (
                      <motion.span
                        key={index}
                        className="tech-tag"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {tech}
                      </motion.span>
                    )
                  )}
                </div>
              </motion.div>

              {/* Key Features */}
              <motion.div className="project-features" variants={itemVariants}>
                <h3>Key Features</h3>
                <ul className="features-list">
                  {(project.features || generateFeatures(project)).map(
                    (feature, index) => (
                      <motion.li
                        key={index}
                        variants={itemVariants}
                        className="feature-item"
                      >
                        <span className="feature-bullet">✓</span>
                        {feature}
                      </motion.li>
                    )
                  )}
                </ul>
              </motion.div>

              {/* Action Buttons */}
              <motion.div className="modal-actions" variants={itemVariants}>
                {project.live && (
                  <motion.a
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-btn primary"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    <span>View Live Demo</span>
                  </motion.a>
                )}

                {project.github && (
                  <motion.a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-btn secondary"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FontAwesomeIcon icon={faGithubBrand} />
                    <span>View Code</span>
                  </motion.a>
                )}

                <motion.button
                  className="action-btn tertiary"
                  onClick={() => setIsModalOpen(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  <span>Close</span>
                </motion.button>
              </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
